import { useEffect, useState } from "react";
import { api, picUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Shield, CheckCircle2, XCircle, Clock, Loader2, MessageCircle, Copy } from "lucide-react";
import { toast } from "sonner";

const STATUS_TABS = [
  { key: "pending", label: "pending", Icon: Clock, color: "text-[#00FFFF]" },
  { key: "paid", label: "paid", Icon: CheckCircle2, color: "text-[#39FF14]" },
  { key: "rejected", label: "rejected", Icon: XCircle, color: "text-white/40" },
];

/** Random 6-char alphanumeric coupon code, prefix MTRX-.
 *  Non-cryptographic; the admin can regenerate freely. */
const makeCoupon = () => {
  const chars = "ABCDEFGHJKLMNPQRSTUVWXYZ23456789"; // no O/0/I/1 ambiguity
  let out = "";
  for (let i = 0; i < 6; i += 1) out += chars[Math.floor(Math.random() * chars.length)];
  return `MTRX-${out}`;
};

/** Build the WhatsApp message template for a payout. */
const buildWaMessage = (p, coupon) => {
  const username = p.user?.username || "there";
  const amount = ((p.net_amount_cents ?? p.amount_cents) / 100).toFixed(2);
  if (p.method === "shop") {
    const code = coupon || "MTRX-XXXXXX";
    return (
      `Hey @${username}! Your $${amount} Matrix Shop credit is ready.\n\n` +
      `Coupon code: ${code}\n` +
      `Redeem at: https://ios.matrixshopping.com\n\n` +
      `Thanks for creating on MatrixShopping.`
    );
  }
  // MMG or PayPal — payment reference is optional
  return (
    `Hey @${username}! Your $${amount} MMG payout has been sent.\n\n` +
    `You should see the funds in your Mobile Money wallet shortly.\n\n` +
    `Thanks for creating on MatrixShopping.`
  );
};

export default function AdminPayouts() {
  const { user } = useAuth();
  const [tab, setTab] = useState("pending");
  const [payouts, setPayouts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [forbidden, setForbidden] = useState(false);
  const [actingOn, setActingOn] = useState(null);
  // Coupon codes generated in this session, keyed by payout id, so the
  // mark-paid modal can pre-fill the code the admin just sent.
  const [lastCoupons, setLastCoupons] = useState({});
  // Payout currently being marked paid via the modal, plus its form state.
  const [paidPayout, setPaidPayout] = useState(null);
  const [paidCoupon, setPaidCoupon] = useState("");
  const [paidNote, setPaidNote] = useState("");

  const load = () => {
    setLoading(true);
    api
      .get(`/admin/payouts?status=${tab}`)
      .then((r) => {
        setPayouts(r.data.payouts || []);
        setForbidden(false);
      })
      .catch((e) => {
        if (e?.response?.status === 403) setForbidden(true);
        setPayouts([]);
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (user) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, tab]);

  /** Open a WhatsApp chat with a pre-filled message + copy the message to
   *  clipboard so the admin can also paste it into WhatsApp Web / Desktop
   *  where deeplink autofill sometimes misbehaves. */
  const openWhatsAppMessage = async (p) => {
    const phone = p.mmg_phone || p.shop_phone || "";
    const digits = phone.replace(/\D/g, "");
    if (digits.length < 7) {
      toast.error("No valid phone on this payout");
      return;
    }
    const coupon = p.method === "shop" ? makeCoupon() : "";
    // Remember the code so the mark-paid modal can pre-fill it.
    if (coupon) setLastCoupons((prev) => ({ ...prev, [p.id]: coupon }));
    const text = buildWaMessage(p, coupon);
    // Best-effort clipboard copy — some browsers block without user gesture.
    try {
      await navigator.clipboard.writeText(text);
      toast.success(p.method === "shop" ? `Coupon ${coupon} · message copied` : "Message copied");
    } catch {
      /* clipboard denied — the deeplink still opens */
    }
    const url = `https://wa.me/${digits}?text=${encodeURIComponent(text)}`;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  /** Non-shop / non-modal actions still route through here. Shop mark-paid
   *  opens the coupon modal via openMarkPaidModal(). */
  const doAction = async (payout_id, action, extras = {}) => {
    setActingOn(payout_id);
    try {
      await api.post(`/admin/payouts/${payout_id}/action`, {
        action,
        ...extras,
      });
      toast.success(action === "mark_paid" ? "Marked as paid" : "Rejected & refunded");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Action failed");
    } finally {
      setActingOn(null);
    }
  };

  const openMarkPaidModal = (p) => {
    setPaidPayout(p);
    setPaidCoupon(lastCoupons[p.id] || "");
    setPaidNote("");
  };

  const confirmMarkPaid = async () => {
    if (!paidPayout) return;
    const coupon = paidCoupon.trim().toUpperCase();
    const isShop = paidPayout.method === "shop";
    if (isShop && !coupon) {
      toast.error("Enter the coupon code you sent the creator");
      return;
    }
    await doAction(paidPayout.id, "mark_paid", {
      admin_note: paidNote.trim() || undefined,
      coupon_code: coupon || undefined,
    });
    setPaidPayout(null);
    setPaidCoupon("");
    setPaidNote("");
  };

  const rejectPayout = async (payout_id) => {
    const note = window.prompt("Reason for rejection (shown to creator)") || "";
    await doAction(payout_id, "reject", { admin_note: note });
  };

  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center text-white/50 font-mono text-xs uppercase tracking-widest">
        sign in required
      </div>
    );
  }

  if (forbidden) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-3">
        <Shield className="h-8 w-8 text-[#FF007F]" />
        <p className="font-mono text-xs uppercase tracking-widest text-white/60">
          admin access required
        </p>
        <p className="text-white/40 text-xs max-w-md text-center px-6">
          Add your user_id to <code className="text-[#39FF14]">ADMIN_USER_IDS</code> in{" "}
          <code>backend/.env</code> and restart the backend.
        </p>
      </div>
    );
  }

  return (
    <div className="md:ml-60 min-h-screen pb-24 md:pb-12">
      <div className="max-w-4xl mx-auto px-6 pt-10">
        <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#FF007F] mb-2">
          // admin.console
        </p>
        <h1
          className="font-display font-black text-4xl mb-6 flex items-center gap-3 glitch"
          data-text="PAYOUT QUEUE"
        >
          <Shield className="h-7 w-7 text-[#FF007F]" strokeWidth={2} />
          PAYOUT QUEUE
        </h1>

        <div className="flex items-center border border-white/15 w-fit mb-6">
          {STATUS_TABS.map((t) => (
            <button
              key={t.key}
              data-testid={`admin-tab-${t.key}`}
              onClick={() => setTab(t.key)}
              className={`px-4 py-2 font-mono text-[10px] uppercase tracking-widest transition-colors flex items-center gap-2 ${
                tab === t.key
                  ? "bg-[#39FF14] text-black font-bold"
                  : `${t.color} hover:text-white`
              }`}
            >
              <t.Icon className="h-3 w-3" />
              {t.label}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="py-20 flex items-center justify-center">
            <Loader2 className="h-6 w-6 text-[#39FF14] animate-spin" />
          </div>
        ) : payouts.length === 0 ? (
          <div
            data-testid="admin-payouts-empty"
            className="py-16 text-center font-mono text-xs uppercase tracking-widest text-white/30"
          >
            // no {tab} payouts
          </div>
        ) : (
          <ul className="space-y-3">
            {payouts.map((p) => (
              <li
                key={p.id}
                data-testid={`admin-payout-${p.id}`}
                className="p-4 border border-white/10 bg-[#0A0A0C] hover:border-[#FF007F]/40 transition-colors"
              >
                <div className="flex items-center gap-3 mb-3">
                  {p.user?.picture ? (
                    <img
                      src={picUrl(p.user.picture)}
                      alt=""
                      className="h-10 w-10 object-cover border border-white/10 flex-shrink-0"
                    />
                  ) : (
                    <div className="h-10 w-10 bg-[#FF007F]/20 flex items-center justify-center text-[#FF007F] font-bold flex-shrink-0">
                      {(p.user?.name || p.user?.username || "?")[0]?.toUpperCase()}
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="text-white font-semibold truncate">
                      {p.user?.name || p.user?.username || p.user_id}
                    </p>
                    <p className="font-mono text-[10px] uppercase tracking-widest text-white/40">
                      @{p.user?.username || "—"} · id {p.user_id.slice(0, 12)}
                    </p>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <p
                      data-testid={`payout-net-${p.id}`}
                      className="font-display font-black text-2xl text-[#39FF14]"
                    >
                      ${((p.net_amount_cents ?? p.amount_cents) / 100).toFixed(2)}
                    </p>
                    <p className="font-mono text-[10px] text-white/40">
                      {p.net_amount_cents != null && p.net_amount_cents !== p.amount_cents ? (
                        <>
                          <span className="text-[#FF007F]">pay this</span>
                          {" · "}from ${(p.amount_cents / 100).toFixed(2)}
                        </>
                      ) : (
                        <>{p.coins.toLocaleString()} coins</>
                      )}
                    </p>
                  </div>
                </div>

                <div className="p-3 bg-black border border-white/5 mb-3">
                  <p className="font-mono text-[10px] uppercase tracking-widest text-white/40">
                    {p.method === "mmg"
                      ? "mmg.number"
                      : p.method === "shop"
                      ? p.shop_phone
                        ? "shop.whatsapp"
                        : "shop.handle"
                      : "paypal.email"}
                    {p.method && (
                      <span
                        data-testid={`payout-method-${p.id}`}
                        className="ml-2 px-1.5 py-0.5 border border-[#FF007F]/50 text-[#FF007F]"
                      >
                        {p.method}
                      </span>
                    )}
                  </p>
                  {(() => {
                    const dest = p.mmg_phone || p.shop_phone || p.shop_handle || p.paypal_email || "—";
                    const digits = String(dest).replace(/\D/g, "");
                    const isPhone = (p.mmg_phone || p.shop_phone) && digits.length >= 7;
                    if (isPhone) {
                      return (
                        <a
                          href={`https://wa.me/${digits}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          data-testid={`payout-destination-${p.id}`}
                          className="font-mono text-sm text-[#00FFFF] break-all select-all hover:underline hover:text-[#39FF14] inline-flex items-center gap-1.5"
                          title="Open WhatsApp chat"
                        >
                          {dest}
                          <span className="text-[10px] uppercase tracking-widest text-[#39FF14]/80">
                            ↗ whatsapp
                          </span>
                        </a>
                      );
                    }
                    return (
                      <p
                        data-testid={`payout-destination-${p.id}`}
                        className="font-mono text-sm text-[#00FFFF] break-all select-all"
                      >
                        {dest}
                      </p>
                    );
                  })()}
                </div>

                <p className="font-mono text-[10px] uppercase tracking-widest text-white/30 mb-3">
                  requested {new Date(p.created_at).toLocaleString()}
                  {p.paid_at && ` · paid ${new Date(p.paid_at).toLocaleString()}`}
                </p>

                {p.coupon_code && (
                  <div
                    data-testid={`payout-coupon-${p.id}`}
                    className="mb-3 border border-[#39FF14]/40 bg-[#39FF14]/5 px-3 py-2 flex items-center justify-between gap-2"
                  >
                    <div className="min-w-0">
                      <p className="font-mono text-[10px] uppercase tracking-widest text-white/40">
                        coupon.sent
                      </p>
                      <p className="font-mono text-sm text-[#39FF14] break-all select-all">
                        {p.coupon_code}
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={() => {
                        navigator.clipboard?.writeText(p.coupon_code).then(
                          () => toast.success("Coupon copied"),
                          () => toast.error("Copy failed"),
                        );
                      }}
                      className="h-8 w-8 border border-white/15 hover:border-[#39FF14]/60 text-white/60 hover:text-[#39FF14] flex items-center justify-center"
                      title="Copy coupon code"
                    >
                      <Copy className="h-3.5 w-3.5" />
                    </button>
                  </div>
                )}

                {p.admin_note && (
                  <p className="text-xs text-white/50 italic mb-3">
                    admin note: {p.admin_note}
                  </p>
                )}

                {p.status === "pending" && (
                  <div className="flex flex-col gap-2">
                    {(p.mmg_phone || p.shop_phone) && (
                      <button
                        type="button"
                        data-testid={`admin-wa-message-${p.id}`}
                        onClick={() => openWhatsAppMessage(p)}
                        className="w-full bg-black border border-[#25D366]/60 text-[#25D366] font-bold uppercase tracking-widest text-[10px] py-2.5 hover:bg-[#25D366]/10 hover:shadow-[0_0_18px_rgba(37,211,102,0.4)] transition-all inline-flex items-center justify-center gap-2"
                        title={
                          p.method === "shop"
                            ? "Generate coupon & open WhatsApp message"
                            : "Open WhatsApp with pre-filled message"
                        }
                      >
                        <MessageCircle className="h-3.5 w-3.5" strokeWidth={2.2} />
                        {p.method === "shop" ? "coupon + WhatsApp" : "WhatsApp message"}
                        <Copy className="h-3 w-3 opacity-60" />
                      </button>
                    )}
                    <div className="flex gap-2">
                      <button
                        data-testid={`admin-mark-paid-${p.id}`}
                        disabled={actingOn === p.id}
                        onClick={() => openMarkPaidModal(p)}
                        className="flex-1 bg-[#39FF14] text-black font-bold uppercase tracking-widest text-[10px] py-2.5 hover:shadow-[0_0_18px_rgba(57,255,20,0.5)] transition-all disabled:opacity-30 flex items-center justify-center gap-1"
                      >
                        <CheckCircle2 className="h-3.5 w-3.5" />
                        mark paid
                      </button>
                      <button
                        data-testid={`admin-reject-${p.id}`}
                        disabled={actingOn === p.id}
                        onClick={() => rejectPayout(p.id)}
                        className="flex-1 bg-black border border-[#FF007F]/50 text-[#FF007F] font-bold uppercase tracking-widest text-[10px] py-2.5 hover:bg-[#FF007F]/10 transition-all disabled:opacity-30 flex items-center justify-center gap-1"
                      >
                        <XCircle className="h-3.5 w-3.5" />
                        reject & refund
                      </button>
                    </div>
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>

      {paidPayout && (
        <div
          data-testid="mark-paid-modal"
          className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={(e) => {
            if (e.target === e.currentTarget) setPaidPayout(null);
          }}
        >
          <div className="w-full max-w-md bg-[#0A0A0C] border border-[#39FF14]/40 shadow-[0_0_40px_rgba(57,255,20,0.15)] p-6">
            <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#FF007F] mb-1">
              // mark.paid
            </p>
            <h2 className="font-display font-black text-2xl text-white mb-4">
              Confirm Payout
            </h2>

            <div className="mb-4 p-3 bg-black border border-white/10 text-xs">
              <p className="text-white/60">
                @{paidPayout.user?.username || paidPayout.user_id}
                <span className="ml-2 text-[#FF007F]">{paidPayout.method}</span>
              </p>
              <p className="font-display font-black text-2xl text-[#39FF14] mt-1">
                ${((paidPayout.net_amount_cents ?? paidPayout.amount_cents) / 100).toFixed(2)}
              </p>
            </div>

            {paidPayout.method === "shop" && (
              <div className="mb-4">
                <label className="font-mono text-[10px] uppercase tracking-widest text-white/60 block mb-2">
                  coupon.code <span className="text-[#FF007F]">*</span>
                </label>
                <div className="flex gap-2">
                  <input
                    data-testid="mark-paid-coupon-input"
                    type="text"
                    value={paidCoupon}
                    onChange={(e) => setPaidCoupon(e.target.value.toUpperCase())}
                    placeholder="MTRX-XXXXXX"
                    className="flex-1 bg-black border border-white/15 focus:border-[#39FF14] text-[#39FF14] font-mono text-sm px-3 py-2 outline-none"
                  />
                  <button
                    type="button"
                    data-testid="mark-paid-coupon-generate"
                    onClick={() => setPaidCoupon(makeCoupon())}
                    className="px-3 py-2 border border-white/15 text-white/60 hover:text-[#39FF14] hover:border-[#39FF14]/60 font-mono text-[10px] uppercase tracking-widest"
                  >
                    gen
                  </button>
                </div>
                <p className="font-mono text-[10px] text-white/30 mt-1">
                  the exact code you sent the creator (for disputes).
                </p>
              </div>
            )}

            <div className="mb-5">
              <label className="font-mono text-[10px] uppercase tracking-widest text-white/60 block mb-2">
                admin.note <span className="text-white/30">(optional)</span>
              </label>
              <textarea
                data-testid="mark-paid-note-input"
                value={paidNote}
                onChange={(e) => setPaidNote(e.target.value)}
                rows={2}
                className="w-full bg-black border border-white/15 focus:border-[#00FFFF] text-white font-mono text-sm px-3 py-2 outline-none resize-none"
              />
            </div>

            <div className="flex gap-2">
              <button
                data-testid="mark-paid-cancel"
                type="button"
                onClick={() => setPaidPayout(null)}
                className="flex-1 bg-black border border-white/15 text-white/60 font-bold uppercase tracking-widest text-[10px] py-2.5 hover:text-white"
              >
                cancel
              </button>
              <button
                data-testid="mark-paid-confirm"
                type="button"
                disabled={actingOn === paidPayout.id}
                onClick={confirmMarkPaid}
                className="flex-1 bg-[#39FF14] text-black font-bold uppercase tracking-widest text-[10px] py-2.5 hover:shadow-[0_0_18px_rgba(57,255,20,0.5)] disabled:opacity-40 flex items-center justify-center gap-1"
              >
                {actingOn === paidPayout.id ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                ) : (
                  <CheckCircle2 className="h-3.5 w-3.5" />
                )}
                confirm paid
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
